<script lang="ts" setup>
import Mousetrap from 'mousetrap';
import { onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import useSettingStore from '../../stores/setting';
import useUiStore from '../../stores/ui';
import useLocale from '../../composables/useLocale';
import AudioVolumeBtn from './AudioVolumeBtn.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faVolumeHigh, faVolumeLow, faVolume, faVolumeSlash, faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import {
  faVolumeHigh as faVolumeHighRegular,
  faVolumeLow as faVolumeLowRegular,
  faVolume as faVolumeRegular,
  faVolumeSlash as faVolumeSlashRegular,
} from '@fortawesome/pro-regular-svg-icons';

import useReaderStore from '../../stores/reader';
import type { FontAwesomeIconSizes } from '../../types';

library.add([
  faVolumeHigh,
  faVolumeLow,
  faVolume,
  faVolumeSlash,
  faMinus,
  faPlus,
  faVolumeHighRegular,
  faVolumeLowRegular,
  faVolumeRegular,
  faVolumeSlashRegular,
]);

type Props = {
  audioBtnIconSize?: FontAwesomeIconSizes;
  audioBtnSize?: number;
};

withDefaults(defineProps<Props>(), {
  audioBtnIconSize: 'xl',
  audioBtnSize: 48,
});

const volumeStep = 0.1;
const volumeMin = 0.0;
const volumeMax = 1.0;

const { t } = useLocale();
const { muted, audioVolume } = storeToRefs(useSettingStore());
const uiStore = useUiStore();
const { isControlsVisible, isVolumeSliderVisible } = storeToRefs(uiStore);
const {
  toggleDrawerAndControls,
  toggleVisibleVolumeSlider,
  closeVisiblePlaybackRate,
  closeVisibleSettings,
  closeLanguageMenu,
} = uiStore;

const { isAudioOnly } = storeToRefs(useReaderStore());

const audioVolumeBtn = ref<HTMLElement | null>(null);

const showToolBars = () => toggleDrawerAndControls(false, true);

const handleChange = (value: number) => {
  audioVolume.value = value;
};

const closeControlOverlays = () => {
  closeVisiblePlaybackRate();
  closeVisibleSettings();
  closeLanguageMenu();
};

onMounted(() => {
  Mousetrap.bind(['m'], () => {
    showToolBars();
    muted.value = !muted.value;
    return false;
  });

  Mousetrap.bind(['up'], () => {
    showToolBars();
    if (audioVolumeBtn.value) {
      toggleVisibleVolumeSlider(true);
    }
    muted.value = false;
    audioVolume.value = Math.min(0.1 + audioVolume.value, 1.0);
    return false;
  });

  Mousetrap.bind(['down'], () => {
    showToolBars();
    if (audioVolumeBtn.value) {
      toggleVisibleVolumeSlider(true);
    }
    muted.value = false;
    audioVolume.value = Math.max(-0.1 + audioVolume.value, 0.0);
    return false;
  });
});

const increaseVolume = () => {
  audioVolume.value = Math.min(volumeStep + audioVolume.value, volumeMax);
};
const decreaseVolume = () => {
  audioVolume.value = Math.max(audioVolume.value - volumeStep, volumeMin);
};

watch(isControlsVisible, (visible) => {
  if (!visible) {
    toggleVisibleVolumeSlider(false);
  }
});
</script>

<template>
  <v-menu
    id="volume-control"
    v-model="isVolumeSliderVisible"
    :close-on-content-click="false"
    open-on-hover
    :persistent="false"
    location="top"
    close-delay="500"
  >
    <template #activator="{ props: menuProps }">
      <div class="d-flex mt-1" :class="isAudioOnly ? 'bg-transparent' : 'bg-brand'">
        <v-sheet class="bg-transparent">
          <AudioVolumeBtn
            ref="audioVolumeBtn"
            v-bind="{ ...menuProps }"
            :is-controls-visible="isControlsVisible"
            :show-tooltips="false"
            @mouseenter="closeControlOverlays"
            @focusin="closeControlOverlays"
            :btn-size="audioBtnSize"
            :icon-size="audioBtnIconSize"
          />
        </v-sheet>
      </div>
    </template>
    <v-card
      :class="{ 'ml-8': isAudioOnly, card: !isAudioOnly }"
      max-width="80"
      :color="isAudioOnly ? 'surface' : 'inverse-surface'"
    >
      <v-card-text class="pa-0">
        <v-slider
          id="volume-slider"
          :color="isAudioOnly ? 'inverse-surface' : 'surface'"
          hide-details
          :title="t('label_volume_slider')"
          :aria-label="t('label_volume_slider')"
          :model-value="audioVolume"
          class="ma-6 text-on-surface"
          direction="vertical"
          :max="volumeMax"
          :step="volumeStep"
          :min="volumeMin"
          @update:modelValue="handleChange"
        >
          <template v-slot:prepend>
            <v-btn
              size="48"
              variant="text"
              :title="t('label_decrease_volume')"
              :aria-label="t('label_decrease_volume')"
              @click="decreaseVolume"
              color="on-surface"
            >
              <FontAwesomeIcon icon="fa-solid fa-minus"></FontAwesomeIcon>
            </v-btn>
          </template>

          <template v-slot:append>
            <v-btn
              size="48"
              variant="text"
              :title="t('label_increase_volume')"
              :aria-label="t('label_increase_volume')"
              @click="increaseVolume"
              color="on-surface"
            >
              <FontAwesomeIcon icon="fa-solid fa-plus"></FontAwesomeIcon>
            </v-btn>
          </template>
        </v-slider>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<style lang="scss" scoped>
.card {
  left: -6px;
}

.v-btn--variant-plain {
  opacity: 1;
}

::v-deep(.v-slider.v-input--vertical .v-input__control) {
  min-height: 165px;
}
</style>
